import React, { useState, useEffect } from "react";
import Layout from "../../components/layout";
import { FaArrowLeft } from "react-icons/fa";
import { FaFileInvoiceDollar } from "react-icons/fa6";

import styles from "./DeliverOrdersPage.module.scss";
import { useLocation, useNavigate } from "react-router-dom";
import { route } from "../../static/route";
import { useForm, useFieldArray, SubmitHandler, Controller } from "react-hook-form";
import { ICloseFormInput, OrderProps } from "../../types/order";
import { GET, POST } from "../../services/axios.service";
import Select from "react-select";
import { selectStyles } from "../../static/styles";

type ItemOption = {
  value: number;
  label: string;
  in_inch?: number;
};

const DeliverOrdersPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { orders, partyId } = (location.state as { orders: OrderProps[]; partyId: number }) || { orders: [], partyId: null };

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [pdfLoadingDeliveryId, setPdfLoadingDeliveryId] = useState<number | null>(null);
  const [itemOptions, setItemOptions] = useState<ItemOption[]>([]);

  useEffect(() => {
    const fetchItems = async () => {
      try {
        const response: any = await GET("/v1/worker/items", {});
        if (response.data.status === "success") {
          setItemOptions(
            response.data.data.map((item: any) => ({
              value: item.id,
              label: item.name,
              in_inch: item.in_inch,
            }))
          );
        }
      } catch {
        // Failed to fetch items
      }
    };
    fetchItems();
  }, []);

  const {
    handleSubmit,
    register,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ICloseFormInput>({
    defaultValues: {
      orders: orders.map((order) => ({
        order_id: order.id,
        delivered_qty: "" as any,
        close_order: false,
        item_id: order.item_id || undefined,
        name: order.name || "",
        inch: order.inch || 1,
        rate: order.rate || 0,
        pending: 0,
      })),
    },
  });

  const { fields } = useFieldArray({
    control,
    name: "orders",
  });

  const handleDeliveryPaymentPdf = async (deliveryId: number, paymentId: number) => {
    setPdfLoadingDeliveryId(deliveryId);
    try {
      const response: any = await POST("/v1/worker/payments-reports", {
        payment_id: paymentId,
      });
      if (response.data.status === "success" && response.data.data?.download_link) {
        window.open(response.data.data.download_link, "_blank");
      }
    } catch (err) {
      // PDF generation failed
    }
    setPdfLoadingDeliveryId(null);
  };

  const handleGoToBack = () => {
    navigate(-1);
  };

  const onSubmit: SubmitHandler<ICloseFormInput> = async (data) => {
    setIsLoading(true);
    setRequestError(null);

    try {
      const response: any = await POST("/v1/worker/order-close", {
        orders: data.orders,
      });

      if (response.data.status === "success") {
        const paymentId = response.data.payment_id;

        if (paymentId) {
          try {
            const reportResponse: any = await POST("/v1/worker/payments-reports", {
              payment_id: paymentId,
            });

            if (reportResponse.data.status === "success" && reportResponse.data.data?.download_link) {
              window.open(reportResponse.data.data.download_link, "_blank");
            }
          } catch (err) {
            // PDF generation failed but delivery was successful
          }
        }

        navigate(route.openOrder);
      } else {
        setRequestError(response.data.message);
      }
    } catch (error: any) {
      setRequestError(error?.data?.message || "Something went wrong. Please try again.");
    }

    setIsLoading(false);
  };

  if (!orders || orders.length === 0) {
    return (
      <Layout>
        <div className={styles.DeliverOrdersPage}>
          <div className="w-full px-10 h-[200px] text-center flex flex-col justify-center relative">
            <FaArrowLeft
              onClick={handleGoToBack}
              className="absolute top-5 left-5 text-3xl text-white border-2 border-white p-1 rounded-full"
            />
            <h1 className="text-5xl mt-6 font-bold text-white">Deliver Orders</h1>
          </div>
          <div className="w-full relative mt-[70px] px-10 py-5 text-center">
            <p className="mt-4 text-primary-2 font-bold text-2xl">No orders selected.</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className={styles.DeliverOrdersPage}>
        <div className="w-full px-10 h-[200px] text-center flex flex-col justify-center relative">
          <FaArrowLeft
            onClick={handleGoToBack}
            className="absolute top-5 left-5 text-3xl text-white border-2 border-white p-1 rounded-full"
          />
          <h1 className="text-5xl mt-6 font-bold text-white">Deliver Orders</h1>
        </div>

        <div className="w-full relative mt-[70px] px-10 py-5">
          <form onSubmit={handleSubmit(onSubmit)}>
            {requestError !== null && (
              <p className="text-red-500 text-center mb-5 font-semibold">{requestError}</p>
            )}

            {fields.map((field, index) => {
              const order = orders[index];
              return (
                <div key={field.id} className="w-full p-4 bg-primary-2 bg-opacity-50 rounded-xl shadow mb-5">
                  <h5 className="text-primary-1 text-lg font-semibold">
                    Order ID : {order.id}
                  </h5>

                  <div className="flex gap-4 mt-3">
                    <img
                      className="w-[80px] h-[80px] bg-white object-contain"
                      src={`${order.image}?order=${order.id}`}
                      alt=""
                    />
                    <div className="flex flex-col gap-1">
                      <p className="text-primary-1 text-sm font-semibold">
                        Item : {order.name ? `${order.name} - ` : ""}{order.item?.name || "-"}
                        {order.inch > 1 && ` ${order.inch} Inch`}
                      </p>
                      <p className="text-primary-1 text-sm font-semibold">
                        Process : {order.process.name}
                      </p>
                      <p className="text-primary-1 text-sm font-semibold">
                        Qty : {order.qty}
                      </p>
                      {order.deliveries && order.deliveries.length > 0 && (
                        <p className="text-primary-1 text-sm font-semibold">
                          Delivered Qty : {order.deliveries.reduce((sum, d) => sum + d.qty, 0)}
                        </p>
                      )}
                    </div>
                  </div>

                  {order.deliveries && order.deliveries.length > 0 && (
                    <div className="mt-3 p-3 bg-white/30 rounded-lg">
                      <p className="text-primary-1 text-sm font-bold mb-2">Delivery History:</p>
                      {order.deliveries.map((delivery) => (
                        <div key={delivery.id} className="flex items-center justify-between text-primary-1 text-sm py-1 border-b border-primary-1/10 last:border-b-0">
                          <span>Qty: {delivery.qty}</span>
                          <div className="flex items-center gap-3">
                            <span>{new Date(delivery.created_at).toLocaleDateString()}</span>
                            {delivery.payment_id && (
                              <button
                                type="button"
                                className="text-primary-1 disabled:opacity-50"
                                onClick={() => handleDeliveryPaymentPdf(delivery.id, delivery.payment_id!)}
                                disabled={pdfLoadingDeliveryId === delivery.id}
                              >
                                <FaFileInvoiceDollar className="text-lg" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <input type="hidden" {...register(`orders.${index}.order_id` as const)} />

                  {(() => {
                    const hasExistingItem = !!order.item_id;
                    return (
                      <>
                        <div className="mt-4">
                          <label className="text-primary-1 text-sm font-semibold block mb-1">
                            Item
                          </label>
                          <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-12 w-full relative rounded">
                            <Controller
                              control={control}
                              name={`orders.${index}.item_id` as const}
                              render={({ field: { value, onChange } }) => (
                                <Select
                                  isClearable
                                  isDisabled={hasExistingItem}
                                  value={
                                    hasExistingItem
                                      ? { value: order.item_id, label: order.item?.name || "" }
                                      : itemOptions?.find((o) => o.value === value) || null
                                  }
                                  className="text-primary-1 p-0 font-semibold text-lg placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                                  styles={selectStyles}
                                  placeholder="Select item"
                                  options={itemOptions}
                                  onChange={(val: any) => {
                                    onChange(val?.value || undefined);
                                    if (val) {
                                      if (!val.in_inch) setValue(`orders.${index}.inch`, 1);
                                    }
                                  }}
                                />
                              )}
                            />
                          </div>
                        </div>

                        <div className="mt-3">
                          <label className="text-primary-1 text-sm font-semibold block mb-1">
                            Name
                          </label>
                          <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-12 w-full relative rounded">
                            <input
                              className="text-primary-1 px-3 py-1 font-semibold text-lg placeholder:text-primary-2/70 focus:outline-none absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded disabled:bg-gray-100"
                              type="text"
                              placeholder="Enter name"
                              disabled={hasExistingItem}
                              {...register(`orders.${index}.name` as const)}
                            />
                          </div>
                        </div>
                      </>
                    );
                  })()}

                  <div className="mt-3">
                    <label className="text-primary-1 text-sm font-semibold block mb-1">
                      Delivered Qty
                    </label>
                    <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-12 w-full relative rounded">
                      <input
                        className="text-primary-1 px-3 py-1 font-semibold text-lg placeholder:text-primary-2/70 focus:outline-none absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                        type="number"
                        pattern="[0-9]*"
                        placeholder="Enter delivered qty"
                        {...register(`orders.${index}.delivered_qty` as const, {
                          required: true,
                          min: 1,
                          valueAsNumber: true,
                        })}
                        onFocus={(e) =>
                          e.target.addEventListener(
                            "wheel",
                            function (e) {
                              e.preventDefault();
                            },
                            { passive: false }
                          )
                        }
                      />
                    </div>
                    {errors.orders?.[index]?.delivered_qty && (
                      <span className="text-red-500 text-sm">Enter delivered qty</span>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div>
                      <label className="text-primary-1 text-sm font-semibold block mb-1">
                        Inch
                      </label>
                      <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-12 w-full relative rounded">
                        <input
                          className="text-primary-1 px-3 py-1 font-semibold text-lg placeholder:text-primary-2/70 focus:outline-none absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded disabled:bg-gray-100"
                          type="number"
                          step="0.1"
                          placeholder="Inch"
                          {...register(`orders.${index}.inch` as const, {
                            valueAsNumber: true,
                          })}
                          disabled={!!order.item_id || (() => {
                            const selectedItemId = watch(`orders.${index}.item_id`);
                            const selectedItem = itemOptions?.find((o) => o.value === selectedItemId);
                            return selectedItem ? !selectedItem.in_inch : false;
                          })()}
                          onFocus={(e) =>
                            e.target.addEventListener(
                              "wheel",
                              function (e) {
                                e.preventDefault();
                              },
                              { passive: false }
                            )
                          }
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-primary-1 text-sm font-semibold block mb-1">
                        Rate
                      </label>
                      <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-12 w-full relative rounded">
                        <input
                          className="text-primary-1 px-3 py-1 font-semibold text-lg placeholder:text-primary-2/70 focus:outline-none absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded disabled:bg-gray-100"
                          type="number"
                          step="any"
                          placeholder="Rate"
                          disabled={!!order.item_id}
                          {...register(`orders.${index}.rate` as const, {
                            valueAsNumber: true,
                          })}
                          onFocus={(e) =>
                            e.target.addEventListener(
                              "wheel",
                              function (e) {
                                e.preventDefault();
                              },
                              { passive: false }
                            )
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 flex items-center gap-2">
                    <input
                      type="checkbox"
                      id={`close_order_${index}`}
                      className="w-5 h-5 accent-primary-1"
                      {...register(`orders.${index}.close_order` as const)}
                    />
                    <label
                      htmlFor={`close_order_${index}`}
                      className="text-primary-1 text-sm font-semibold cursor-pointer"
                    >
                      Close Order (fully delivered)
                    </label>
                  </div>

                  {watch(`orders.${index}.close_order`) && (
                    <div className="mt-3">
                      <label className="text-primary-1 text-sm font-semibold block mb-1">
                        Pending
                      </label>
                      <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-12 w-full relative rounded">
                        <input
                          className="text-primary-1 px-3 py-1 font-semibold text-lg placeholder:text-primary-2/70 focus:outline-none absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                          type="number"
                          pattern="[0-9]*"
                          placeholder="Enter pending qty"
                          {...register(`orders.${index}.pending` as const, {
                            valueAsNumber: true,
                          })}
                          onFocus={(e) =>
                            e.target.addEventListener(
                              "wheel",
                              function (e) {
                                e.preventDefault();
                              },
                              { passive: false }
                            )
                          }
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            <div className="w-full flex">
              <button
                className="mx-auto mt-4 mb-8 shadow rounded-3xl text-3xl text-white bg-gradient-to-r from-primary-1 to-primary-2 font-bold px-10 py-2 disabled:opacity-50"
                type="submit"
                disabled={isLoading}
              >
                {isLoading ? "Please wait..." : "Submit"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
};
export default DeliverOrdersPage;
